'use client';

import { useEffect, useMemo, memo } from 'react';
import { useCookies } from 'next-client-cookies';
import { LineItem } from 'shared/types/wishlist';
import { EU_LOCALE, getLocalizationInfo, NORTH_AMERICA_LOCALES } from 'project.config';
import { useAccount, useProjectSettings, useWishlist } from 'frontastic';
import { setFieldsFromProjectSettings, useAnalytics } from '..';

export enum UserType {
  PROSPECT = 'prospect',
  CUSTOMER = 'customer',
}

interface GASnippetProps {
  pageName?: string;
}

const ONE_TRUST_POLL_INTERVAL_MS = 200;
const ONE_TRUST_POLL_TIMEOUT_MS = 15_000;

const GASnippet = ({ pageName }: GASnippetProps) => {
  const cookies = useCookies();
  const locale = cookies.get('locale') as string;
  const { localeName, countryCode } = getLocalizationInfo(locale);
  const region = NORTH_AMERICA_LOCALES.includes(countryCode) ? countryCode : EU_LOCALE;
  const oneTrustLanguage = locale?.split(/[-_]/)[0]?.toLowerCase() ?? 'en';
  const analytics = useAnalytics();
  const { initialize, lastInitialData } = analytics;
  // Runs during render (before any effects commit) so other analytics events queue behind this page's data_layer_ready.
  analytics.markPageChange?.(pageName);
  const { projectSettings } = useProjectSettings();
  const { gaTrackingId, previewModeParams } = projectSettings ?? {};
  const { account } = useAccount();
  const { accountId, email } = account || {};
  const { data: wishlist } = useWishlist();
  const hasPastOrders = cookies.get('hasPastOrders') === 'true';

  // Memoize analyticsInitialData to avoid unnecessary recalculations
  const analyticsInitialData = useMemo(
    () => ({
      event: 'data_layer_ready',
      user_id: accountId,
      content_group: pageName,
      user_data: { email: btoa(email as string) },
      site_region: region,
      site_language: localeName,
    }),
    [accountId, email, localeName, pageName, region],
  );

  useEffect(() => {
    let cancelled = false;
    if (!gaTrackingId || !wishlist) return;

    setFieldsFromProjectSettings(gaTrackingId, previewModeParams);

    const fetchAndInit = async () => {
      // For active draft projects, we'll fetch their mediaclip project IDs from Wishlist API as happening in MyProject component
      const projectsActive = wishlist?.lineItems?.flatMap((li: LineItem) => {
        const id = li?.custom?.fields?.mediaClipProjectId;
        return id ? [id] : [];
      });

      if (!cancelled) {
        const currentInitialData = {
          ...analyticsInitialData,
          user_type: hasPastOrders ? UserType.CUSTOMER : UserType.PROSPECT,
          projects_active: projectsActive,
          projects_active_count: projectsActive?.length,
        };

        const shouldRetrigger =
          !!lastInitialData &&
          !!currentInitialData &&
          (lastInitialData?.content_group !== currentInitialData?.content_group ||
            lastInitialData?.projects_active_count !== currentInitialData?.projects_active_count ||
            lastInitialData?.site_language !== currentInitialData?.site_language ||
            lastInitialData?.user_type !== currentInitialData?.user_type);

        initialize(currentInitialData, shouldRetrigger);
      }
    };

    void fetchAndInit();

    return () => {
      cancelled = true;
    };
  }, [gaTrackingId, previewModeParams, analyticsInitialData, accountId, wishlist, hasPastOrders]);

  // Safety net for the html lang attribute: GTM injects OneTrust asynchronously, so poll for it.
  useEffect(() => {
    if (!oneTrustLanguage) return;

    let languageApplied = false;

    const applyLanguage = () => {
      if (languageApplied || !window.OneTrust?.changeLanguage) return false;

      languageApplied = true;

      try {
        window.OneTrust.changeLanguage(oneTrustLanguage);
      } catch (error) {
        console.warn('Unable to set OneTrust language', error);
      }

      return true;
    };

    if (applyLanguage()) return;

    const pollInterval = window.setInterval(() => {
      if (applyLanguage()) window.clearInterval(pollInterval);
    }, ONE_TRUST_POLL_INTERVAL_MS);

    const pollDeadline = window.setTimeout(() => window.clearInterval(pollInterval), ONE_TRUST_POLL_TIMEOUT_MS);

    return () => {
      languageApplied = true;
      window.clearInterval(pollInterval);
      window.clearTimeout(pollDeadline);
    };
  }, [oneTrustLanguage]);

  return null;
};

export default memo(GASnippet);
